import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiRequest } from 'utils/api';

// ==============================|| INITIAL STATE ||============================== //

interface Task {
  id: number;
  externalId: string;
  title: string;
  description: string | null;
  status: string;
  source: string;
  issueType: string | null;
  priority: string | null;
  repositoryId: number | null;
  assignedTo: string | null;
  createdAt: string;
  updatedAt: string;
}

interface TasksState {
  tasks: Task[];
  currentTask: Task | null;
  status: 'idle' | 'loading' | 'success' | 'failed';
  error: string;
}

const initialState: TasksState = {
  tasks: [],
  currentTask: null,
  status: 'idle',
  error: '',
};

// ==============================|| ASYNC THUNKS ||============================== //

export const getTasks = createAsyncThunk(
  'tasks/getTasks',
  async (params: { repositoryId?: number; status?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.repositoryId) queryParams.append('repositoryId', params.repositoryId.toString());
      if (params.status) queryParams.append('status', params.status);

      const endpoint = `/tasks${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiRequest(endpoint);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const cancelTask = createAsyncThunk(
  'tasks/cancelTask',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const response = await apiRequest(`/tasks/${taskId}/cancel`, {
        method: 'POST',
      });
      return response.response || response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteTask = createAsyncThunk(
  'tasks/deleteTask',
  async (taskId: number, { rejectWithValue }) => {
    try {
      await apiRequest(`/tasks/${taskId}`, {
        method: 'DELETE',
      });
      return taskId;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==============================|| ASYNC ACTION HANDLERS ||============================== //

const asyncActionHandlers = {
  // Get tasks
  [getTasks.pending.type]: { status: 'loading' as const },
  [getTasks.fulfilled.type]: (state: TasksState, action: any) => {
    if (!action.payload) {
      state.tasks = [];
      state.status = 'success';
      state.error = '';
      return;
    }
    const tasks = Array.isArray(action.payload.response)
      ? action.payload.response
      : action.payload.response?.tasks || action.payload.tasks || [];

    state.tasks = tasks;
    state.status = 'success';
    state.error = '';
  },
  [getTasks.rejected.type]: (state: TasksState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to fetch tasks';
  },

  // Cancel task
  [cancelTask.pending.type]: { status: 'loading' as const },
  [cancelTask.fulfilled.type]: (state: TasksState, action: any) => {
    const updatedTask = action.payload;
    if (updatedTask?.id) {
      const index = state.tasks.findIndex((t) => t.id === updatedTask.id);
      if (index !== -1) {
        state.tasks[index] = { ...state.tasks[index], ...updatedTask };
      }
    }
    state.status = 'success';
    state.error = '';
  },
  [cancelTask.rejected.type]: (state: TasksState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to cancel task';
  },

  // Delete task
  [deleteTask.pending.type]: { status: 'loading' as const },
  [deleteTask.fulfilled.type]: (state: TasksState, action: any) => {
    const taskId = action.payload;
    state.tasks = state.tasks.filter((t) => t.id !== taskId);
    if (state.currentTask?.id === taskId) {
      state.currentTask = null;
    }
    state.status = 'success';
    state.error = '';
  },
  [deleteTask.rejected.type]: (state: TasksState, action: any) => {
    state.status = 'failed';
    state.error = action.payload || 'Failed to delete task';
  },
};

// ==============================|| SLICE ||============================== //

export const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    clearError(state) {
      state.error = '';
    },
    setCurrentTask(state, action) {
      state.currentTask = action.payload;
    },
    clearTasksState(state) {
      state.tasks = [];
      state.currentTask = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    Object.entries(asyncActionHandlers).forEach(([type, handler]) => {
      builder.addCase(type, (state, action) => {
        if (typeof handler === 'function') {
          handler(state, action);
        } else {
          const options = handler;
          state.status = options.status;
        }
      });
    });
  },
});

// ==============================|| ACTIONS ||============================== //

export const { clearError, setCurrentTask, clearTasksState } = tasksSlice.actions;

// ==============================|| SELECTORS ||============================== //

export const selectTasks = (state: any) => state.tasks.tasks;
export const selectCurrentTask = (state: any) => state.tasks.currentTask;
export const selectTasksStatus = (state: any) => state.tasks.status;
export const selectTasksError = (state: any) => state.tasks.error;

export default tasksSlice.reducer;
